/**
 * ============================================================================
 * CapaRow — table row component for the CAPA workbench.
 * Owner: Phase 7 — CAPA Module agent.
 * URS rows: CAPA-001..010.
 * ============================================================================ */

import { useTranslation } from 'react-i18next';
import styles from './CapaWorkbench.module.css';
import type { CapaSummary } from './useCapasQuery';

export interface CapaRowProps {
  capa: CapaSummary;
  onOpen: (c: CapaSummary) => void;
}

function statusClass(status: CapaSummary['status']): string {
  switch (status) {
    case 'IDENTIFIED':         return styles.statusIdentified;
    case 'INVESTIGATION':      return styles.statusInvestigation;
    case 'ACTION_PLAN':        return styles.statusActionPlan;
    case 'IMPLEMENTATION':     return styles.statusImplementation;
    case 'EFFECTIVENESS_CHECK': return styles.statusEffectivenessCheck;
    case 'CLOSED':             return styles.statusClosed;
    case 'CANCELLED':          return styles.statusCancelled;
    default:                   return styles.statusIdentified;
  }
}

function critClass(c: CapaSummary['criticality']): string {
  switch (c) {
    case 'CRITICAL': return styles.critCritical;
    case 'MAJOR':    return styles.critMajor;
    case 'MINOR':    return styles.critMinor;
    default:         return styles.critMinor;
  }
}

export function CapaRow({ capa, onOpen }: CapaRowProps) {
  const { t, i18n } = useTranslation();
  const df = new Intl.DateTimeFormat(i18n.language, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  return (
    <tr>
      <td>
        <button
          type="button"
          className={styles.linkBtn}
          onClick={() => onOpen(capa)}
          aria-label={t('capa.row.openAria', {
            defaultValue: 'Open {{number}}',
            number: capa.capaNumber,
          })}
        >
          {capa.capaNumber}
        </button>
      </td>
      <td>{capa.title}</td>
      <td>
        <span className={`${styles.chip} ${statusClass(capa.status)}`}>
          {capa.status}
        </span>
      </td>
      <td>
        <span className={`${styles.chip} ${critClass(capa.criticality)}`}>
          {capa.criticality}
        </span>
      </td>
      <td>{capa.sourceKind}</td>
      <td>{capa.dueDateUtc ? df.format(new Date(capa.dueDateUtc)) : '—'}</td>
      <td>
        <span
          className={`${styles.chip} ${
            capa.hasEffectivenessVerification
              ? styles.badgeVerified
              : styles.badgeUnverified
          }`}
        >
          {capa.hasEffectivenessVerification
            ? t('capa.row.verifiedYes', { defaultValue: 'Verified' })
            : t('capa.row.verifiedNo', { defaultValue: 'Pending' })}
        </span>
      </td>
    </tr>
  );
}
